import type {
  EvalAnnotation,
  FeedbackTraceResponse,
  TraceCitation,
} from "../types";
import type {
  GoldenRow,
  GoldenRowExpectedCitation,
  GoldenRowStewardEdits,
} from "../types/goldenRow";
import { deriveGoldenRowAuthor } from "./deriveGoldenRowAuthor";
import { extractPrecedingUserQuestion } from "./extractPrecedingUserQuestion";

function formatAuthoredAt(date = new Date()): string {
  return date.toISOString().slice(0, 10);
}

function buildDefaultGoldenId(jurisdiction: string): string {
  return `${jurisdiction}-<number>`;
}

function mapCitation(citation: TraceCitation): GoldenRowExpectedCitation {
  return {
    org_category: citation.org_category,
    org_id: citation.org_id,
    doc_id: citation.doc_id,
    doc_name: citation.doc_name ?? null,
    clause_hint: null,
    required: true,
  };
}

function buildAnnotationNotes(annotation?: EvalAnnotation): string | null {
  const parts = [
    annotation?.reviewer_notes,
    annotation?.sanitisation_notes,
  ].filter((value): value is string => Boolean(value));

  return parts.length > 0 ? parts.join("\n\n") : null;
}

export function buildGoldenRow(
  trace: FeedbackTraceResponse,
  annotation: EvalAnnotation | undefined,
  reviewerIdentity: string,
  stewardEdits: GoldenRowStewardEdits = {},
): GoldenRow {
  const jurisdiction = trace.jurisdiction ?? "nz";
  const assistantMessage =
    trace.messages[trace.selected_assistant_message_index];
  const citations = assistantMessage?.citations?.map(mapCitation) ?? [];

  const defaults: GoldenRow = {
    id: buildDefaultGoldenId(jurisdiction),
    schema_version: 1,
    authored_at: formatAuthoredAt(),
    author: deriveGoldenRowAuthor(reviewerIdentity),
    jurisdiction,
    available_org_keys:
      trace.knowledge_orgs?.map((org) => ({
        id: org.id,
        category: org.category,
      })) ?? [],
    question: extractPrecedingUserQuestion(
      trace.messages,
      trace.selected_assistant_message_index,
    ),
    expected_citations: citations,
    expected_answer_keywords: [],
    expected_clarification: null,
    min_retrieval_precision: 0,
    min_retrieval_recall: 1,
    notes: buildAnnotationNotes(annotation),
  };

  return {
    ...defaults,
    ...stewardEdits,
    available_org_keys:
      stewardEdits.available_org_keys ?? defaults.available_org_keys,
    expected_citations:
      stewardEdits.expected_citations ?? defaults.expected_citations,
    expected_answer_keywords:
      stewardEdits.expected_answer_keywords ??
      defaults.expected_answer_keywords,
  };
}
